import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { STAY_PLAN_TABLES, type StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import type { PlanBirthdayPet } from "./scheduleModel";

export const activePlanBirthdayPetsKey = (clinicId: string | null) => ["active-plan-birthday-pets", clinicId];

type ContractRow = {
  pet_id: string;
  remaining_days: number | null;
  pets: { name: string; birth_date: string | null; owners: { name: string } | null } | null;
};

/** `status = "active"` and no exhausted day package — same "active" definition as `partitionStayPlanContracts`. */
const isActiveContract = (row: ContractRow) => row.remaining_days === null || row.remaining_days > 0;

async function fetchModuleBirthdayPets(module: StayPlanModule, clinicId: string): Promise<PlanBirthdayPet[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from(STAY_PLAN_TABLES[module].contracts)
    .select("pet_id, remaining_days, pets ( name, birth_date, owners ( name ) )")
    .eq("clinic_id", clinicId)
    .eq("status", "active");
  if (error) throw error;

  return ((data ?? []) as ContractRow[])
    .filter(isActiveContract)
    .map((row) => ({
      petId: row.pet_id,
      petName: row.pets?.name ?? "",
      ownerName: row.pets?.owners?.name ?? "",
      birthDate: row.pets?.birth_date ?? null,
      module,
    }));
}

/**
 * Pets with an active daycare or hotel plan contract, for the calendar's
 * birthday indicator. Not scoped to a date range: an active plan isn't tied
 * to a specific day, so month/day matching happens per grid cell instead.
 */
export function useActivePlanBirthdayPets() {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: activePlanBirthdayPetsKey(clinicId),
    enabled: !!clinicId,
    queryFn: async (): Promise<PlanBirthdayPet[]> => {
      if (!clinicId) return [];
      const [daycarePets, hotelPets] = await Promise.all([
        fetchModuleBirthdayPets("daycare", clinicId),
        fetchModuleBirthdayPets("hotel", clinicId),
      ]);
      return [...daycarePets, ...hotelPets];
    },
  });
}
